import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../utils/connect';
import { Formik } from 'formik';

import { Card, Row, Col, Flex } from '@app/ui';
import sportsApi from '../utils/sportsApi';
import pacingActions from '../actions/pacing';
import { CreatePacingContext } from './CreatePacingContext';

import CreatePacingForm from './CreatePacingForm';
import getIds from '../utils/getIdsFromSelectedObject';
import getUseRightsholderAttribution from '../utils/getUseRightsholderAttribution';

import './pacing.scss';
import { parseStartDate, parseEndDate } from '../utils/parseDate';
import { useNavigate } from 'react-router-dom';

// Helpers

const validateForm = values => {
    let errors = {};

    // Check if required fileds are set
    if (!values.rightsholder) {
        errors.rightsholder = 'this field is required';
    }

    if (!values.sponsor) {
        errors.sponsor = 'this field is required';
    }

    if (getIds(values.platformTypes).length === 0) {
        errors.platformTypes = 'this field is required';
    }

    if (!values.metric) {
        errors.metric = 'this field is required';
    }

    if (!values.targetValue) {
        errors.targetValue = 'target value must be greater than 0';
    }

    if (!values.name) {
        errors.name = 'Please select a unique name for this goal';
    }

    // endDate needs a startDate
    if (!values.startDate && values.endDate) {
        errors.startDate = 'Required if an "end date" was selected.';
    }

    if (values.endDate < values.startDate) {
        errors.endDate = 'Cannot set an end date before a start date.';
        errors.startDate = 'Cannot set an start date after a end date.';
    }

    if (values.endDate < Date.now()) {
        errors.endDate = 'Cannot set an end date in the past';
    }

    return errors;
};

const CreatePacing = ({ client, sendNotification, getLimit, pacing }) => {
    const { t } = useTranslation('tools');
    const navigate = useNavigate();
    const { areAllPlacementsSelected, userActivities } =
        useContext(CreatePacingContext);
    const { availableGoalCount, limit } = pacing;

    useEffect(() => {
        getLimit();
    }, []);

    useEffect(() => {
        if (availableGoalCount <= 0 && Number.isFinite(limit)) {
            navigate(`/c/${client.id}/pacing`);
        }
    }, [availableGoalCount, navigate]);

    const handleSubmit = async (values, actions) => {
        actions.setStatus({
            isCreatingGoal: true,
        });
        const {
            name,
            rightsholder,
            placements,
            sponsor,
            platformTypes,
            startDate,
            endDate,
            metric,
            targetValue,
        } = values;

        const dates = {};
        if (startDate) {
            const value = new Date(startDate);
            value.setHours(0, 0, 0, 0);
            dates.startDate = parseStartDate(value);
        }

        if (endDate) {
            const value = new Date(endDate);
            value.setHours(23, 59, 59, 999);
            dates.endDate = parseEndDate(value);
        }

        try {
            const useRightsholderAttribution = getUseRightsholderAttribution(
                client,
                client.id,
            );
            const payload = {
                action: 'create',
                domainObject: {
                    name,
                    ...dates,
                    isUsingRha: useRightsholderAttribution,
                    metricId: metric,
                    targetValue: parseInt(targetValue),
                    filter: {
                        rightsholderIds: [rightsholder],
                        sponsorIds: [sponsor],
                        rightsholderPlacementIds: areAllPlacementsSelected
                            ? []
                            : getIds(placements),
                        platformTypeIds: getIds(platformTypes),
                    },
                },
                calculateHelpedGoalTargetEvents: userActivities,
            };

            const result = await sportsApi
                .url(`/clients/${client.id}/goals`)
                .json(payload)
                .post()
                .res();

            if (result.status === 200) {
                sendNotification(
                    t('pacing.messages.success.goalProcessing', { name }),
                    { context: 'success' },
                );
                navigate(`/c/${client.id}/pacing`);
            }
        } finally {
            actions.setStatus({
                isCreatingGoal: false,
            });
        }
    };

    return (
        <Flex vertical className="gds-layout__container">
            <Row gutter={16} className="-m-t-4 -m-t-3-sm">
                <Col
                    md={24}
                    className="gds-flex gds-flex--justify-between gds-flex--align-center -m-b-3"
                >
                    <h1 data-test="title" className="gds-text--header-md">
                        {t('pacing.pageTitle')}
                    </h1>
                </Col>
            </Row>
            <Row gutter={16} className="-m-t-2 -m-t-3-sm">
                <Col md={24}>
                    <Card className="-p-a-3 gds-flex gds-flex gds-flex--justify-around gds-flex--align-stretch">
                        <div className="gds-layout__column--md-12 gds-layout__column--xs-12">
                            <Formik
                                initialValues={{
                                    name: '',
                                    rightsholder: undefined,
                                    sponsor: undefined,
                                    placements: {},
                                    platformTypes: {},
                                    startDate: undefined,
                                    endDate: undefined,
                                    metric: undefined,
                                    targetValue: undefined,
                                }}
                                initialStatus={{
                                    isSubmitting: false,
                                }}
                                validate={validateForm}
                                validateOnBlur
                                validateOnChange
                                onSubmit={handleSubmit}
                            >
                                {({
                                    values,
                                    errors,
                                    status,
                                    setFieldValue,
                                }) => {
                                    return (
                                        <CreatePacingForm
                                            values={values}
                                            errors={errors}
                                            status={status}
                                            setFieldValue={setFieldValue}
                                        />
                                    );
                                }}
                            </Formik>
                        </div>
                    </Card>
                </Col>
            </Row>
        </Flex>
    );
};

CreatePacing.propTypes = {
    client: PropTypes.object.isRequired,
    sendNotification: PropTypes.func,
    getLimit: PropTypes.func,
    pacing: PropTypes.object.isRequired,
};

export default connect(['client', 'pacing'], pacingActions)(CreatePacing);
